import { Suspense, lazy } from "react";
import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppShell } from "@/components/emc/shell/AppShell";
import { LoadingState } from "@/components/emc/states";
import { useAuth } from "@/lib/auth";
import CampaignDetail from "@/pages/campaigns/CampaignDetail";
import CampaignList from "@/pages/campaigns/CampaignList";
import CampaignEditor from "@/pages/campaigns/editor/CampaignEditor";
import Contacts, { ContactDetailPage } from "@/pages/Contacts";
import Deliverability from "@/pages/Deliverability";
import Events from "@/pages/Events";
import FlowDetail from "@/pages/flows/FlowDetail";
import FlowList from "@/pages/flows/FlowList";
import FlowComparison from "@/pages/flows/FlowComparison";
import FlowComparisonList from "@/pages/flows/FlowComparisonList";
import Login from "@/pages/Login";
import Overview from "@/pages/Overview";
import Pivot from "@/pages/Pivot";
import Reports from "@/pages/Reports";
import CampaignReportPage from "@/pages/reports/CampaignReport";
import Segments from "@/pages/Segments";
import SendingAccounts from "@/pages/SendingAccounts";
import Suppressions from "@/pages/Suppressions";
import TemplateGallery from "@/pages/templates/TemplateGallery";
import { SET_PASSWORD_PATH } from "@/pages/users/model";
import Profile from "@/pages/users/Profile";
import SetPassword from "@/pages/users/SetPassword";
import UsersSettings from "@/pages/users/UsersSettings";
import PoolDetail from "@/pages/warmup/PoolDetail";
import WarmupBoard from "@/pages/warmup/WarmupBoard";
import Watchdog from "@/pages/Watchdog";

/** A page only an admin is led to (4.5). The API refuses everybody else
 * anyway; this sends a typed-in address back to the start rather than to a
 * screen of 403s. */
function AdminOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return user?.role === "admin" ? <>{children}</> : <Navigate to="/" replace />;
}

// The template editor is GrapesJS, most of the admin's weight: its own chunk,
// loaded when a template is opened, so every other screen starts without it.
const TemplateEditorPage = lazy(() => import("@/pages/templates/editor/TemplateEditorPage"));
// The flow canvas is React Flow: its own chunk too, for the same reason (M7).
const FlowEditorPage = lazy(() => import("@/pages/flows/editor/FlowEditorPage"));
const FlowHistoryPage = lazy(() => import("@/pages/flows/editor/FlowHistoryPage"));

// The screens that exist. The navigation that leads to them — grouped, in
// the order a marketer works — is components/emc/shell/nav.ts.
const PAGES: Record<string, JSX.Element> = {
  // A campaign is a group over its countries (4.3); the list, the editor's
  // five steps and one campaign's page (UI-2). "uj" is a static segment, so
  // it wins over ":id" whatever the order.
  "/kampanyok": <CampaignList />,
  "/kampanyok/uj": <CampaignEditor />,
  "/kampanyok/:id": <CampaignDetail />,
  "/kampanyok/:id/szerkesztes": <CampaignEditor />,
  // The Klaviyo flows step by step (7.2 M1); the canvas, the editor of a
  // draft and the versions side by side (7.3 M7).
  "/flowk": <FlowList />,
  "/flowk/:id": <FlowDetail />,
  // The daily comparison with Klaviyo (7.2 M4): every flow, and one flow.
  // "osszevetes" is a static segment, so it wins over ":id".
  "/flowk/osszevetes": <FlowComparisonList />,
  "/flowk/:id/osszevetes": <FlowComparison />,
  "/flowk/:id/vaszon": (
    <Suspense fallback={<LoadingState label="A vászon betöltése…" className="p-8" />}>
      <FlowEditorPage />
    </Suspense>
  ),
  "/flowk/:id/vaszon/:number": (
    <Suspense fallback={<LoadingState label="A vászon betöltése…" className="p-8" />}>
      <FlowEditorPage />
    </Suspense>
  ),
  "/flowk/:id/verziok": (
    <Suspense fallback={<LoadingState label="A verziók betöltése…" className="p-8" />}>
      <FlowHistoryPage />
    </Suspense>
  ),
  // The list, a new segment and one segment (UI-4): pages/Segments.tsx routes them.
  "/szegmensek/*": <Segments />,
  "/kontaktok": <Contacts />,
  // One address in full, under the list for the same reason the editor is
  // under the templates: the id in the path is the contact it is open on, and
  // there is no such page without one.
  "/kontaktok/:id": <ContactDetailPage />,
  "/kizarasok": <Suppressions />,
  "/sablonok": <TemplateGallery />,
  // The editor lives under the list rather than beside it: the id in the path
  // is the template it is open on, and there is no editor without one.
  "/sablonok/:id": (
    <Suspense fallback={<LoadingState label="A szerkesztő betöltése…" className="p-8" />}>
      <TemplateEditorPage />
    </Suspense>
  ),
  "/kezbesites": <Deliverability />,
  // Whose name each country goes out under, and whether that is fit to send
  // from (0.1). Read-only: the values arrive by migration.
  "/kuldo-fiokok": <SendingAccounts />,
  // What the outside world says about us, and what the watchdog did (2.5).
  "/orszem": <Watchdog />,
  // Every pool's warmup, and one pool's curve, days and log (UI-6).
  "/warmup": <WarmupBoard />,
  "/warmup/:code": <PoolDetail />,
  // What the webshop sent us: the flow engine's triggers (7.2 reads them).
  "/esemenyek": <Events />,
  // The pivot is its own route rather than a tab: it answers a different
  // question (what a tag value was worth) and a link to it is worth having.
  "/riportok": <Reports />,
  "/riportok/pivot": <Pivot />,
  // One campaign's report (UI-7): a group across its countries, or the group
  // a country campaign belongs to.
  "/riportok/csoport/:id": <CampaignReportPage by="group" />,
  "/riportok/kampany/:id": <CampaignReportPage by="campaign" />,
  // Users and the link to the country settings (4.5): admins only.
  "/beallitasok": (
    <AdminOnly>
      <UsersSettings />
    </AdminOnly>
  ),
  // Every user's own name and password (4.5), from the menu under the name.
  "/profilom": <Profile />,
};

// The kit with invented numbers, for `vite dev` only: the production build
// replaces import.meta.env.DEV with false and drops the page altogether.
const KitPreview = import.meta.env.DEV ? lazy(() => import("@/dev/KitPreview")) : null;

function Shell() {
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Overview />} />
        {Object.entries(PAGES).map(([path, element]) => (
          <Route key={path} path={path} element={element} />
        ))}
        {KitPreview && (
          <Route
            path="/_kit"
            element={
              <Suspense fallback={<LoadingState />}>
                <KitPreview />
              </Suspense>
            }
          />
        )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}

export default function App() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  // The set-password page an invitation opens: public, outside the shell,
  // whoever is or is not signed in (4.5).
  if (pathname === SET_PASSWORD_PATH) return <SetPassword />;
  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center px-4">
        <LoadingState lines={0} />
      </div>
    );
  }
  return user === null ? <Login /> : <Shell />;
}
